import PropTypes from "prop-types";
import { useCallback, useEffect, useMemo, useState } from "react";
import { Helmet } from "react-helmet-async";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import {
  ArrowLeft,
  ChevronLeft,
  ChevronRight,
  ExternalLink,
  FileText,
} from "lucide-react";
import { Button } from "components/ui/button";
import { Card, CardContent } from "components/ui/card";
import { Checkbox } from "components/ui/checkbox";
import { Label } from "components/ui/label";
import { useCampaignProject } from "hooks/useCampaignProject";
import {
  buildRiskDisclosurePath,
  parseInvestmentFlowSearchParams,
  validateBeforeInvest,
} from "lib/investmentHelper";
import swtAlert from "lib/sweetAlert";
import { OPPORTUNITIES_PATH } from "lib/opportunitiesNav";
import { cn } from "lib/utils";

function isPdfDocument(doc) {
  if (!doc?.url) return false;
  const type = String(doc.type || "").toUpperCase();
  if (type === "PDF") return true;
  const path = String(doc.url).split("?")[0].toLowerCase();
  return path.endsWith(".pdf");
}

async function showPreflightAlert(preflight) {
  if (preflight.severity === "error") {
    await swtAlert.error(preflight.title, preflight.message);
    return;
  }
  await swtAlert.warning(preflight.title, preflight.message);
}

function buildInvestValidationInput(flow, campaign) {
  return {
    projectId: flow.campaignId,
    amount: flow.amount,
    minInvestment: flow.minInvestment || campaign?.minInvestment,
    maxInvestment: flow.maxInvestment ?? campaign?.maxInvestment,
    campaignStartAt: campaign?.campaignStartAt,
    campaignEndAt: campaign?.campaignEndAt,
    daysRemaining: campaign?.daysRemaining,
    status: campaign?.status,
    canInvest: campaign?.canInvest,
  };
}

export function getOfferingDocumentLabel(index) {
  return `Document ${index + 1}`;
}

/** All offering documents that have a URL. */
export function getViewableOfferingDocuments(documents = []) {
  return documents.filter((d) => String(d?.url || "").trim());
}

export default function OfferingDocumentPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const flow = parseInvestmentFlowSearchParams(searchParams);
  const { campaignId, amount, minInvestment, maxInvestment } = flow;

  const { campaign, loading, error } = useCampaignProject(campaignId);
  const [acknowledgedByIndex, setAcknowledgedByIndex] = useState({});
  const [activeDocIndex, setActiveDocIndex] = useState(0);

  const viewableDocs = useMemo(
    () => getViewableOfferingDocuments(campaign?.documents),
    [campaign?.documents]
  );

  const docCount = viewableDocs.length;
  const hasMultipleDocs = docCount > 1;
  const safeIndex = docCount > 0 ? Math.min(activeDocIndex, docCount - 1) : 0;
  const activeDoc = viewableDocs[safeIndex] ?? null;

  const allDocumentsAcknowledged = useMemo(
    () => docCount > 0 && viewableDocs.every((_, index) => acknowledgedByIndex[index] === true),
    [docCount, viewableDocs, acknowledgedByIndex]
  );

  const setDocAcknowledged = useCallback((index, checked) => {
    setAcknowledgedByIndex((prev) => ({ ...prev, [index]: checked === true }));
  }, []);

  useEffect(() => {
    setActiveDocIndex(0);
    setAcknowledgedByIndex({});
  }, [campaignId, docCount]);

  const goToPreviousDoc = useCallback(() => {
    setActiveDocIndex((i) => Math.max(0, i - 1));
  }, []);

  const goToNextDoc = useCallback(() => {
    setActiveDocIndex((i) => Math.min(docCount - 1, i + 1));
  }, [docCount]);

  useEffect(() => {
    const onKeyDown = (e) => {
      if (!hasMultipleDocs) return;
      if (e.key === "ArrowLeft") {
        e.preventDefault();
        goToPreviousDoc();
      } else if (e.key === "ArrowRight") {
        e.preventDefault();
        goToNextDoc();
      }
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [hasMultipleDocs, goToPreviousDoc, goToNextDoc]);

  const formattedAmount = Number.isFinite(amount)
    ? new Intl.NumberFormat("en-US", {
        style: "currency",
        currency: "USD",
        minimumFractionDigits: 0,
        maximumFractionDigits: 0,
      }).format(amount)
    : null;

  const handleProceed = async () => {
    if (!allDocumentsAcknowledged) {
      const pending = viewableDocs
        .map((_, index) => (acknowledgedByIndex[index] ? null : getOfferingDocumentLabel(index)))
        .filter(Boolean);
      await swtAlert.warning(
        "Acknowledgement required",
        pending.length > 0
          ? `Please read and acknowledge ${pending.join(", ")} before continuing.`
          : "Please confirm that you have read and understood each offering document."
      );
      return;
    }

    const preflight = validateBeforeInvest(buildInvestValidationInput(flow, campaign));

    if (!preflight.ok) {
      await showPreflightAlert(preflight);
      return;
    }

    navigate(
      buildRiskDisclosurePath(
        campaignId,
        amount,
        minInvestment || campaign?.minInvestment,
        maxInvestment ?? campaign?.maxInvestment
      )
    );
  };

  if (!campaignId) {
    return (
      <FlowMessage
        title="Missing campaign"
        message="Return to the campaign and start the investment process again."
        backTo="/opportunities"
        backLabel="Browse opportunities"
      />
    );
  }

  const showDocumentViewer = !loading && Boolean(activeDoc?.url);
  const showEmptyDocuments = !loading && !error && docCount === 0;
  const atFirstDoc = safeIndex <= 0;
  const atLastDoc = safeIndex >= docCount - 1;

  return (
    <>
      <Helmet>
        <title>Offering document | Fortify</title>
      </Helmet>

      <section className="py-12 sm:py-16 bg-secondary/30 min-h-[70vh]">
        <div className="container mx-auto px-4 sm:px-6 lg:px-12 xl:px-16 max-w-5xl">
          <Link
            to={`/campaign/${campaignId}`}
            className="inline-flex items-center gap-1.5 text-base text-accent font-medium hover:underline mb-8"
          >
            <ArrowLeft className="h-5 w-5" />
            Back to campaign
          </Link>

          <div className="mb-8">
            <p className="text-sm sm:text-base text-muted-foreground uppercase tracking-wide">
              Step 1 of 3
            </p>
            <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight mt-2">
              Offering document
            </h1>
            {campaign?.title ? (
              <p className="mt-3 text-base text-muted-foreground">{campaign.title}</p>
            ) : null}
            {formattedAmount ? (
              <p className="mt-2 text-base font-medium tabular-nums">
                Investment amount: {formattedAmount}
              </p>
            ) : null}
            {hasMultipleDocs ? (
              <p className="mt-2 text-sm text-muted-foreground">
                Review each document and acknowledge every one before proceeding.
              </p>
            ) : null}
          </div>

          {loading ? (
            <p className="text-muted-foreground text-center py-16 text-base">
              Loading documents…
            </p>
          ) : null}

          {error ? (
            <p className="text-destructive text-center py-8 text-base">{error}</p>
          ) : null}

          {showEmptyDocuments ? (
            <EmptyDocumentsCard campaignId={campaignId} />
          ) : null}

          {showDocumentViewer ? (
            <OfferingDocumentViewer
              activeDoc={activeDoc}
              safeIndex={safeIndex}
              docCount={docCount}
              hasMultipleDocs={hasMultipleDocs}
              viewableDocs={viewableDocs}
              atFirstDoc={atFirstDoc}
              atLastDoc={atLastDoc}
              acknowledgedByIndex={acknowledgedByIndex}
              allDocumentsAcknowledged={allDocumentsAcknowledged}
              onDocAcknowledgedChange={setDocAcknowledged}
              onPreviousDoc={goToPreviousDoc}
              onNextDoc={goToNextDoc}
              onSelectDoc={setActiveDocIndex}
              onProceed={handleProceed}
            />
          ) : null}
        </div>
      </section>
    </>
  );
}

function EmptyDocumentsCard({ campaignId }) {
  return (
    <Card>
      <CardContent className="py-12 text-center">
        <FileText className="mx-auto h-10 w-10 text-muted-foreground mb-4" />
        <p className="text-muted-foreground text-base">
          No offering document is available for this campaign.
        </p>
        <Button variant="outline" className="mt-6" asChild>
          <Link to={`/campaign/${campaignId}`}>Back to campaign</Link>
        </Button>
      </CardContent>
    </Card>
  );
}

EmptyDocumentsCard.propTypes = {
  campaignId: PropTypes.string.isRequired,
};

function OfferingDocumentViewer({
  activeDoc,
  safeIndex,
  docCount,
  hasMultipleDocs,
  viewableDocs,
  atFirstDoc,
  atLastDoc,
  acknowledgedByIndex,
  allDocumentsAcknowledged,
  onDocAcknowledgedChange,
  onPreviousDoc,
  onNextDoc,
  onSelectDoc,
  onProceed,
}) {
  const activeLabel = getOfferingDocumentLabel(safeIndex);
  const activeIsPdf = isPdfDocument(activeDoc);

  return (
    <>
      <Card className="shadow-md overflow-hidden mb-6">
        <CardContent className="p-0">
          <div className="flex flex-col gap-3 border-b px-4 py-3 sm:px-5 bg-muted/40 sm:flex-row sm:items-center sm:justify-between">
            <div className="min-w-0 flex-1">
              <p className="font-medium text-base truncate">{activeLabel}</p>
              {hasMultipleDocs ? (
                <p className="text-sm text-muted-foreground tabular-nums mt-0.5">
                  {safeIndex + 1} of {docCount}
                </p>
              ) : null}
            </div>

            <div className="flex flex-wrap items-center gap-2 shrink-0">
              {hasMultipleDocs ? (
                <>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={onPreviousDoc}
                    disabled={atFirstDoc}
                    aria-label="Previous document"
                  >
                    <ChevronLeft className="h-4 w-4" />
                    <span className="hidden sm:inline ml-1">Previous</span>
                  </Button>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={onNextDoc}
                    disabled={atLastDoc}
                    aria-label="Next document"
                  >
                    <span className="hidden sm:inline mr-1">Next</span>
                    <ChevronRight className="h-4 w-4" />
                  </Button>
                </>
              ) : null}
              <Button variant="outline" size="sm" asChild>
                <a
                  href={activeDoc.url}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <ExternalLink className="mr-2 h-4 w-4" />
                  Open in new tab
                </a>
              </Button>
            </div>
          </div>

          {hasMultipleDocs ? (
            <div
              className="flex gap-2 overflow-x-auto px-4 py-2 border-b bg-card/80"
              role="tablist"
              aria-label="Offering documents"
            >
              {viewableDocs.map((doc, index) => {
                const label = getOfferingDocumentLabel(index);
                const acknowledged = acknowledgedByIndex[index] === true;
                return (
                  <button
                    key={`${doc.url}-${index}`}
                    type="button"
                    role="tab"
                    aria-selected={index === safeIndex}
                    onClick={() => onSelectDoc(index)}
                    className={cn(
                      "shrink-0 rounded-md px-3 py-1.5 text-sm font-medium transition-colors",
                      index === safeIndex
                        ? "bg-primary text-primary-foreground"
                        : "bg-muted text-muted-foreground hover:bg-muted/80 hover:text-foreground",
                      acknowledged && index !== safeIndex && "ring-1 ring-accent/60"
                    )}
                  >
                    {label}
                    {acknowledged ? " ✓" : ""}
                  </button>
                );
              })}
            </div>
          ) : null}

          {activeIsPdf ? (
            <iframe
              key={`${safeIndex}-${activeDoc.url}`}
              title={activeLabel}
              src={activeDoc.url}
              className="w-full h-[min(70vh,720px)] bg-muted/20"
            />
          ) : (
            <div
              key={`${safeIndex}-${activeDoc.url}`}
              className="flex h-[min(70vh,720px)] flex-col items-center justify-center gap-4 bg-muted/20 p-8 text-center"
            >
              <FileText className="h-12 w-12 text-muted-foreground" />
              <p className="max-w-md text-sm text-muted-foreground">
                Preview is not available for this file type. Open {activeLabel} in a new tab to
                review it before acknowledging.
              </p>
              <Button variant="outline" asChild>
                <a href={activeDoc.url} target="_blank" rel="noopener noreferrer">
                  <ExternalLink className="mr-2 h-4 w-4" />
                  Open {activeLabel}
                </a>
              </Button>
            </div>
          )}
        </CardContent>
      </Card>

      <div className="rounded-lg border bg-card p-5 mb-6 space-y-4">
        <p className="text-sm font-medium">Acknowledge each document</p>
        {viewableDocs.map((doc, index) => {
          const label = getOfferingDocumentLabel(index);
          const checkboxId = `offering-read-ack-${index}`;
          return (
            <div key={`${doc.url}-${index}`} className="flex gap-4 items-start">
              <Checkbox
                id={checkboxId}
                checked={acknowledgedByIndex[index] === true}
                onCheckedChange={(v) => onDocAcknowledgedChange(index, v === true)}
                className="mt-0.5 h-5 w-5"
                aria-required
              />
              <Label
                htmlFor={checkboxId}
                className="text-base leading-relaxed font-normal cursor-pointer"
              >
                I have read and understood {label}
              </Label>
            </div>
          );
        })}
      </div>

      <Button
        type="button"
        size="lg"
        className="w-full sm:w-auto min-w-48 h-12 text-base"
        disabled={!allDocumentsAcknowledged}
        onClick={onProceed}
      >
        Proceed to risk acknowledgement
      </Button>
    </>
  );
}

OfferingDocumentViewer.propTypes = {
  activeDoc: PropTypes.shape({
    name: PropTypes.string,
    url: PropTypes.string.isRequired,
  }).isRequired,
  safeIndex: PropTypes.number.isRequired,
  docCount: PropTypes.number.isRequired,
  hasMultipleDocs: PropTypes.bool.isRequired,
  viewableDocs: PropTypes.arrayOf(
    PropTypes.shape({
      name: PropTypes.string,
      url: PropTypes.string,
    })
  ).isRequired,
  atFirstDoc: PropTypes.bool.isRequired,
  atLastDoc: PropTypes.bool.isRequired,
  acknowledgedByIndex: PropTypes.objectOf(PropTypes.bool).isRequired,
  allDocumentsAcknowledged: PropTypes.bool.isRequired,
  onDocAcknowledgedChange: PropTypes.func.isRequired,
  onPreviousDoc: PropTypes.func.isRequired,
  onNextDoc: PropTypes.func.isRequired,
  onSelectDoc: PropTypes.func.isRequired,
  onProceed: PropTypes.func.isRequired,
};

function FlowMessage({ title, message, backTo, backLabel }) {
  return (
    <section className="py-16">
      <div className="container mx-auto px-4 max-w-lg text-center">
        <h1 className="text-2xl font-semibold">{title}</h1>
        <p className="mt-4 text-muted-foreground">{message}</p>
        <Button
          variant="outline"
          className={cn("mt-8", backTo === OPPORTUNITIES_PATH && "d-none")}
          asChild
        >
          <Link to={backTo}>{backLabel}</Link>
        </Button>
      </div>
    </section>
  );
}

FlowMessage.propTypes = {
  title: PropTypes.string.isRequired,
  message: PropTypes.string.isRequired,
  backTo: PropTypes.string.isRequired,
  backLabel: PropTypes.string.isRequired,
};
